import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../shadcn/components/ui/card";
import { Badge } from "../../../../shadcn/components/ui/badge";
import { Button } from "../../../../shadcn/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../shadcn/components/ui/table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ScrollArea } from "../../../../shadcn/components/ui/scroll-area";
import { cn } from "../../../../shadcn/lib/utils";

interface BiddersInfoSectionProps {
  bidders: any;
  status: string;
}

export default function BiddersInfoSection({
  bidders,
  status,
}: BiddersInfoSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const maskString = (str: string) => {
    if (!str) return "N/A";
    if (status === "live") {
      return str.slice(0, 1) + "XXX" + str.slice(-1);
    }
    return str;
  };

  const maskEmailV2 = (email: string) => {
    if (!email) return "N/A";
    const [local, domain] = email.split("@");
    if (!local || !domain) return "N/A";

    // Mask local part: show middle character with asterisks before and after
    // Format: ***v**** (3+ asterisks, middle char, 4+ asterisks)
    let maskedLocal = "***";
    if (local.length > 2) {
      const middleIndex = Math.floor(local.length / 2);
      maskedLocal = `***${local[middleIndex]}***`;
    } else if (local.length > 0) {
      maskedLocal = "***";
    }

    // Mask domain part: show middle character of domain name, mask rest, then show TLD
    // Format: ***i***.com (3+ asterisks, middle char, 3+ asterisks, TLD)
    const [domainName, ...tldParts] = domain.split(".");
    const tld = tldParts.length > 0 ? `.${tldParts.join(".")}` : "";
    let maskedDomain = "***";
    if (domainName.length > 2) {
      const middleIndex = Math.floor(domainName.length / 2);
      maskedDomain = `***${domainName[middleIndex]}***${tld}`;
    } else if (domainName.length > 0) {
      maskedDomain = `***${tld}`;
    }

    return `${maskedLocal}@${maskedDomain}`;
  };

  const maskEmail = (email: string) => {
    if (!email) return "N/A";
    if (status === "live") {
      return maskEmailV2(email);
    }
    return email;
  };

  const maskMobile = (mobile: string) => {
    if (!mobile) return "N/A";
    if (status === "live") {
      return "+91" + mobile.slice(0, 1) + "XXXXXX" + mobile.slice(-1);
    }
    return "+91" + mobile;
  };

  const getKYCStatus = (bidder: any) => {
    // Try to get from various possible locations
    if (bidder?.kyc_verified || bidder?.meta?.kyc_verified) {
      return { label: "Approved", variant: "kyc-ok" };
    }
    if (
      bidder?.kyc_status === "offline" ||
      bidder?.meta?.kyc_status === "offline"
    ) {
      return { label: "Offline verification", variant: "kyc-offline" };
    }
    if (
      bidder?.digio_request_status === "pending" ||
      bidder?.status === "pending"
    ) {
      return { label: "Pending", variant: "kyc-pending" };
    }
    // Default to approved if we have session data (means they're active)
    if (bidder?.total_connections > 0) {
      return { label: "Approved", variant: "kyc-ok" };
    }
    return { label: "Pending", variant: "kyc-pending" };
  };

  const getEMDStatus = (bidder: any) => {
    if (bidder?.emd_amount_paid || bidder?.meta?.emd_amount_paid) {
      return { label: "Paid", variant: "emd-paid" };
    }
    return { label: "Not paid", variant: "emd-notpaid" };
  };

  const getEligibilityStatus = (bidder: any) => {
    if (bidder?.status === "verified") {
      return { label: "Eligible", variant: "elig-ok" };
    }
    return { label: "Not eligible", variant: "elig-blocked" };
  };

  const getRemarks = (bidder: any) => {
    // Check for same IP alerts or other warnings
    if (bidder?.same_ip_alert || bidder?.meta?.same_ip_alert) {
      return "Same IP alert";
    }
    return "";
  };

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="p-4 pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Bidders Information
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="rounded-full bg-orange-500 text-white hover:bg-orange-600 border-0 h-7 px-3 text-xs flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Hide bidders info
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                View bidders info
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-4 pt-0">
          {bidders.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto border-gray-200 mt-2 max-w-full">
              {/* <ScrollArea className="h-[200px]"> */}
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      S.N.
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      Name
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      Mobile
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      Email
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      KYC
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      EMD
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      Eligibility
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      IP Address
                    </TableHead>
                    <TableHead className="text-xs h-8 font-semibold uppercase tracking-wider px-3 py-2">
                      Remarks
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bidders.map((bidder: any, index: number) => {
                    const kycStatus = getKYCStatus(bidder);
                    const emdStatus = getEMDStatus(bidder);
                    const eligStatus = getEligibilityStatus(bidder);
                    const remarks = getRemarks(bidder);

                    return (
                      <TableRow
                        key={bidder?.user_uuid || index}
                        className={cn(
                          "text-xs",
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        )}
                      >
                        <TableCell className="px-3 py-2">{index + 1}</TableCell>
                        <TableCell className="px-3 py-2 uppercase">
                          {maskString(bidder?.meta?.name)}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          {maskMobile(
                            bidder?.mobile_number ||
                              bidder?.meta?.mobile_number ||
                              bidder?.meta?.user_details?.mobile_number
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          {maskEmail(bidder?.bidder_email || bidder?.email)}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Badge
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              kycStatus.variant === "kyc-ok" &&
                                "bg-green-50 text-green-700",
                              kycStatus.variant === "kyc-offline" &&
                                "bg-yellow-50 text-yellow-800",
                              kycStatus.variant === "kyc-pending" &&
                                "bg-red-50 text-red-700"
                            )}
                          >
                            {kycStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Badge
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              emdStatus.variant === "emd-paid" &&
                                "bg-blue-50 text-blue-800",
                              emdStatus.variant === "emd-notpaid" &&
                                "bg-red-50 text-red-700"
                            )}
                          >
                            {emdStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <Badge
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              eligStatus.variant === "elig-ok" &&
                                "bg-blue-50 text-blue-800",
                              eligStatus.variant === "elig-blocked" &&
                                "bg-red-50 text-red-700"
                            )}
                          >
                            {eligStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <span className="font-mono text-xs text-gray-600">
                            {bidder?.meta?.user_ip || "N/A"}
                          </span>
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          {remarks && (
                            <span className="text-xs text-orange-700">
                              {remarks}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {/* </ScrollArea> */}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 text-sm">
              No bidders information available.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
